/* Reusable form primitives for the vendor application form. */
import { CheckCircle2 } from "lucide-react";

export const Field = ({ children, label, required, hint, testId }) => (
  <div data-testid={testId} className="space-y-2">
    <label className="block text-[11px] tracking-[0.2em] uppercase text-[var(--ld-ink-muted)]">
      {label}
      {required && (
        <span className="ml-1 text-[var(--ld-gold-deep)]">*</span>
      )}
    </label>
    {hint && (
      <p className="text-xs text-[var(--ld-ink-muted)]/80 -mt-1">{hint}</p>
    )}
    {children}
  </div>
);

export const TextInput = ({ id, value, onChange, type = "text", placeholder, readOnly }) => (
  <input
    id={id}
    data-testid={`field-${id}`}
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    readOnly={readOnly}
    className={
      "w-full h-12 px-4 bg-transparent border border-[var(--ld-ink)]/30 text-sm text-[var(--ld-ink)] placeholder:text-[var(--ld-ink-muted)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors duration-300 " +
      (readOnly ? "opacity-70 cursor-not-allowed" : "")
    }
  />
);

export const TextArea = ({ id, value, onChange, rows = 5, placeholder }) => (
  <textarea
    id={id}
    data-testid={`field-${id}`}
    rows={rows}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full px-4 py-3 bg-transparent border border-[var(--ld-ink)]/30 text-sm text-[var(--ld-ink)] placeholder:text-[var(--ld-ink-muted)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors duration-300 resize-none"
  />
);

export const Checkboxes = ({ id, options, value, onChange }) => (
  <div
    data-testid={`field-${id}`}
    className="grid grid-cols-1 sm:grid-cols-2 gap-2"
  >
    {options.map((opt) => {
      const checked = value.includes(opt);
      return (
        <label
          key={opt}
          className={
            "flex items-center gap-3 px-4 py-3 border cursor-pointer transition-colors duration-200 " +
            (checked
              ? "bg-[var(--ld-ink)] text-[var(--ld-gold)] border-[var(--ld-ink)]"
              : "bg-transparent text-[var(--ld-ink)] border-[var(--ld-ink)]/30 hover:border-[var(--ld-ink)]")
          }
        >
          <input
            type="checkbox"
            className="sr-only"
            checked={checked}
            onChange={() => {
              const next = checked
                ? value.filter((v) => v !== opt)
                : [...value, opt];
              onChange(next);
            }}
          />
          <span
            aria-hidden
            className={
              "h-4 w-4 border flex items-center justify-center shrink-0 " +
              (checked
                ? "border-[var(--ld-gold)] bg-[var(--ld-gold)]"
                : "border-[var(--ld-ink)]/40")
            }
          >
            {checked && (
              <CheckCircle2 size={12} strokeWidth={2.5} className="text-[var(--ld-ink)]" />
            )}
          </span>
          <span className="text-sm">{opt}</span>
        </label>
      );
    })}
  </div>
);

export const Radios = ({ id, options, value, onChange }) => (
  <div
    data-testid={`field-${id}`}
    className="grid grid-cols-1 sm:grid-cols-2 gap-2"
  >
    {options.map((opt) => {
      const checked = value === opt;
      return (
        <label
          key={opt}
          className={
            "flex items-center gap-3 px-4 py-3 border cursor-pointer transition-colors duration-200 " +
            (checked
              ? "bg-[var(--ld-ink)] text-[var(--ld-gold)] border-[var(--ld-ink)]"
              : "bg-transparent text-[var(--ld-ink)] border-[var(--ld-ink)]/30 hover:border-[var(--ld-ink)]")
          }
        >
          <input
            type="radio"
            name={id}
            className="sr-only"
            checked={checked}
            onChange={() => onChange(opt)}
          />
          <span
            aria-hidden
            className={
              "h-4 w-4 rounded-full border flex items-center justify-center shrink-0 " +
              (checked
                ? "border-[var(--ld-gold)]"
                : "border-[var(--ld-ink)]/40")
            }
          >
            {checked && (
              <span className="h-2 w-2 rounded-full bg-[var(--ld-gold)]" />
            )}
          </span>
          <span className="text-sm">{opt}</span>
        </label>
      );
    })}
  </div>
);
